'use client';

import * as React from 'react';
import Link from 'next/link';
import { LociWordmark } from '@loci/ui';
import type { CityCode } from '@loci/city-editions';
import {
  GLOBAL_NAV_ACCENT_VARS,
  GN_ACTIVE_LINK_GLOW_STATIC,
} from '@/components/molecules/CityCoordStrip/GlobalNavCoordStrip';
import { CategoriesDropdown } from '@/components/molecules/CategoriesDropdown';
import { WayfinderSheet } from '@/components/molecules/WayfinderSheet';
import { useAuth } from '@/contexts/AuthContext';
import { useGlobalSearchShortcut } from '@/hooks/useGlobalSearchShortcut';
import { useSearchSheet } from '@/hooks/useSearchSheet';
import { useCityContext, useCityIsStaging, useCitySurface } from '@/lib/city-context.client';
import { crossHostLogin } from '@/lib/cross-host-redirect';
import { appUrl, cityAppEntryUrl, cityUrl, defaultCityCode } from '@/lib/cross-host-urls';
import { usePathname } from 'next/navigation';
import { SiteHeaderApexView } from './SiteHeaderApexView';
import { useActiveLink, useDiscoverActive } from './hooks/useActiveLink';
import { useScrolledPast } from './hooks/useScrolledPast';

/**
 * Extract the active category slug from a pathname, supporting both surfaces:
 *  - marketing-city: `/category/<slug>` (e.g. `/category/music`)
 *  - app:            `/<city>/category/<slug>` (e.g. `/clt/category/music`)
 *
 * Returns `undefined` when the pathname does not match either shape so the
 * dropdown trigger remains in the default (non-active) state.
 */
const CATEGORY_PATHNAME_PATTERN = /^\/(?:[^/]+\/)?category\/([^/?#]+)/;

export function extractCategorySlugFromPathname(pathname: string | null): string | undefined {
  if (!pathname) return undefined;
  const match = pathname.match(CATEGORY_PATHNAME_PATTERN);
  return match?.[1];
}

/**
 * Surface where the header is mounted. Drives the variant rendered by the
 * connected wrapper.
 *
 * There is no `'app'` surface: the signed-in per-city app shell renders the
 * dedicated `AppHeader` organism (`AppShellLayout`), and the auth pages
 * (`/login`, `/signup`, …) render `<AuthShell>` — neither mounts `SiteHeader`.
 * The former `SiteHeaderAppView` variant was orphaned dead code (its `/home`
 * wordmark had no route or redirect, and its `/search` link diverged from the
 * SearchSheet trigger used everywhere else); it was removed rather than left
 * carrying a dead link no surface rendered.
 */
export type SiteHeaderSurface = 'apex' | 'marketing';

interface NavItem {
  href: string;
  label: string;
}

/**
 * City-surface nav items — source of truth for labels + paths:
 * `docs/design-system/1.1.0/templates/city/index.html` (header nav row,
 * lines 300-303). The Categories slot is intentionally a flat link in this
 * issue (FRE-819); FRE-827 swaps it for the `CategoriesDropdown` molecule.
 */
const MARKETING_NAV_ITEMS: ReadonlyArray<NavItem> = [
  { href: '/discover', label: 'Discover' },
  { href: '/neighborhoods', label: 'Neighborhoods' },
  { href: '/discover?date=this-week', label: 'This week' },
  { href: '/category/music', label: 'Categories' },
];

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-[#0F141F]';

// WEB-333 — DS 1.2.0 `o-globalnav__link` typography: Space Grotesk 13px / 500.
const PILL_TYPE = 'font-display text-small tracking-[0.005em]';

/**
 * Visual ⌘K affordance shown in the header at `≥ md` viewports. Renders as a
 * button so it remains keyboard-reachable; clicking opens the global
 * `SearchSheet` overlay via `useSearchSheet().setOpen(true)` (FRE-828).
 *
 * The sheet itself is mounted once at the root by `SearchSheetMount`
 * (FRE-816, `apps/web/app/_components/SearchSheetMount.tsx`), so every
 * `SiteHeader` instance — marketing-city, app, or apex (when added) — talks
 * to the same module-scoped `useSearchSheet` store. This unifies the
 * marketing-surface search affordance with the consumer `AppHeader`, which
 * already wires its search button to the same hook.
 *
 * Pre-FRE-828 this dispatched a `discover:focus-search` `CustomEvent` that
 * `useDiscoverSearchFocus` (still mounted via `SiteHeader`) consumed to
 * focus the in-page `DiscoverSearchField`. That field is a Discover-surface
 * concern; the SearchSheet is the global, cross-surface search entry. The
 * ⌘K keyboard shortcut continues to be handled by `useDiscoverSearchFocus`
 * (focuses the discover field, navigates to `/<city>/discover` if needed) —
 * a follow-up issue can route ⌘K to the SearchSheet once product confirms
 * the desired global-shortcut behavior.
 *
 * The glyph defaults to the mac form (`⌘K`) so server-rendered markup matches
 * the most common platform (and the Storybook default). After mount, a
 * one-shot effect inspects `navigator.platform` and swaps to `Ctrl K` on
 * non-mac clients — guarded so SSR and the first client render agree, then
 * the badge updates without flashing layout (text width difference is small).
 */
function KbdSearchHint() {
  const [isMac, setIsMac] = React.useState(true);
  const { setOpen } = useSearchSheet();

  React.useEffect(() => {
    if (typeof navigator === 'undefined') return;
    const platform = navigator.platform ?? '';
    setIsMac(/Mac/i.test(platform));
  }, []);

  const handleClick = React.useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  return (
    <button
      type="button"
      onClick={handleClick}
      aria-label={isMac ? 'Search (Command K)' : 'Search (Control K)'}
      className={[
        // Hidden below md; visible at md+ per AC.
        'hidden shrink-0 items-center gap-1 rounded-md border border-white/[0.14] bg-bg-sunken px-2 py-1 md:inline-flex',
        'font-mono text-micro font-medium leading-none text-white/85',
        'transition-colors duration-150 hover:bg-white/15 motion-reduce:transition-none',
        FOCUS_RING,
      ].join(' ')}
    >
      <kbd className="font-mono">{isMac ? '⌘' : 'Ctrl'}</kbd>
      <kbd className="font-mono">K</kbd>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Shell
// ─────────────────────────────────────────────────────────────────────────────

export interface SiteHeaderShellProps {
  /** Accessible name for the banner landmark. */
  ariaLabel: 'Landing' | 'App';
  /** Wordmark home destination — `/` for marketing, `/home` for the app. */
  wordmarkHref: string;
  /** Accessible name for the wordmark link. */
  wordmarkAriaLabel: string;
  /** Inner contents: nav + actions. */
  children: React.ReactNode;
  className?: string;
}

/**
 * Glass-pill chrome shared by both header variants. Owns the fixed positioning,
 * safe-area-inset top offset, ring, translucent fill, blur, and the wordmark
 * link on the left. Variants supply the contents inside the pill via children.
 */
export function SiteHeaderShell({
  ariaLabel,
  wordmarkHref,
  wordmarkAriaLabel,
  children,
  className = '',
}: SiteHeaderShellProps) {
  return (
    <header
      aria-label={ariaLabel}
      style={GLOBAL_NAV_ACCENT_VARS.city}
      className={`fixed left-1/2 top-[calc(env(safe-area-inset-top)+1.25rem)] z-50 w-[calc(100%-40px)] max-w-[960px] -translate-x-1/2 ${className}`}
    >
      <div className="flex items-center justify-between gap-2 rounded-full border border-white/[0.08] bg-[rgba(15,20,31,0.78)] px-3 py-2 backdrop-blur-[18px] backdrop-saturate-[1.4] sm:gap-4 sm:px-5">
        <Link
          href={wordmarkHref}
          aria-label={wordmarkAriaLabel}
          className={`inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-full ${FOCUS_RING}`}
        >
          <LociWordmark city="CLT" size={16} color="#FFFFFF" cityColor="#4FC4C4" />
        </Link>
        {children}
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Marketing variant
// ─────────────────────────────────────────────────────────────────────────────

interface MarketingNavLinkProps {
  item: NavItem;
  isActive: boolean;
}

function MarketingNavLink({ item, isActive }: MarketingNavLinkProps) {
  return (
    <Link
      href={item.href}
      aria-current={isActive ? 'page' : undefined}
      data-active={isActive ? 'true' : undefined}
      className={[
        'relative inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-full px-2 py-2 sm:px-3',
        PILL_TYPE,
        'font-medium transition-colors duration-150 hover:bg-white/[0.06] motion-reduce:transition-none',
        FOCUS_RING,
        // WEB-333: active = glowing accent underline (DS 1.2.0
        // `o-globalnav__link[aria-current="page"]::after`), not a filled pill.
        isActive ? `text-white ${GN_ACTIVE_LINK_GLOW_STATIC}` : 'text-white/85 hover:text-white',
      ].join(' ')}
    >
      {item.label}
    </Link>
  );
}

export interface SiteHeaderMarketingViewProps {
  /**
   * Nav items to render. Defaults to {@link MARKETING_NAV_ITEMS} so stories and
   * the marketing-city surface keep their bare-path nav. The connected
   * `SiteHeader` overrides this with cross-host hrefs when the marketing
   * variant is rendered on the `app` surface (auth pages), since
   * `/discover`, `/neighborhoods`, `/category/*` only resolve on
   * the marketing-city surface.
   */
  navItems?: ReadonlyArray<NavItem>;
  /** Pre-computed active state per nav item, keyed by href. Used by stories/tests. */
  activeMap?: Readonly<Record<string, boolean>>;
  /** Show signed-in state (hides Sign in / Open app CTAs, shows Account link). */
  isSignedIn?: boolean;
  /** Render the ⌘K kbd affordance. Defaults to true; stories opt out for snapshot stability. */
  showKbdHint?: boolean;
  /**
   * Destination for the primary "Open app" CTA. Defaults to the canonical
   * `cityAppEntryUrl()` (Charlotte). Stories override for snapshot stability.
   */
  openAppHref?: string;
  /**
   * Destination for the signed-in "Account" link. Defaults to the cross-host
   * app-surface profile URL (`appUrl({ city, path: '/profile' })`, Charlotte
   * for the MVP). This MUST be an absolute app-host URL: the marketing variant
   * renders on the marketing-city host (`clt.loci.city`), where a host-relative
   * `/me` (or `/clt/profile`) 404s — the city is the subdomain, not a path
   * segment, so that path shape only resolves on `app.loci.city`. Stories
   * override for snapshot stability.
   */
  accountHref?: string;
  /**
   * FRE-827: optional slot rendered in place of the `Categories` flat nav link.
   * When provided, any `navItems` entry whose href starts with `/category` is
   * replaced by this node (kept inline at the same nav position). The connected
   * `SiteHeader` passes a `<CategoriesDropdown />` here for the city variant so
   * marketing surfaces align with the consumer AppHeader's dropdown affordance.
   * Stories that want to render a flat link instead can omit this prop.
   */
  categoriesSlot?: React.ReactNode;
  /**
   * City the marketing surface is serving. Drives the Wayfinder layer-switcher
   * rendered next to the wordmark — the city/app-layer hrefs and the "Switch
   * city" chip resolve to this edition. Defaults to the registry default
   * (Charlotte for the MVP); the connected `SiteHeader` passes the live
   * `useCityContext()` city.
   */
  cityCode?: CityCode;
  /**
   * Request-host staging flag (WEB-776). Resolved by the connected
   * `SiteHeader` via `useCityIsStaging()` so this View stays prop-driven.
   */
  isStaging?: boolean;
  className?: string;
}

/**
 * Marketing (unauth-first) variant. Nav: Discover / Neighborhoods / This week
 * / Categories — mirrors `docs/design-system/1.1.0/templates/city/index.html`
 * lines 300-303. Right side shows a quiet "Sign in" text link plus the teal
 * "Open app" CTA (matches template `.a-btn--text` + `.a-btn--accent` pairing
 * at lines 307-308) when signed out, or an Account link when signed in. A ⌘K
 * kbd hint appears at `≥ md` viewports as a search shortcut affordance for
 * the Discover surface.
 */
export function SiteHeaderMarketingView({
  navItems = MARKETING_NAV_ITEMS,
  activeMap,
  isSignedIn = false,
  showKbdHint = true,
  openAppHref,
  accountHref,
  categoriesSlot,
  cityCode = defaultCityCode(),
  isStaging,
  className = '',
}: SiteHeaderMarketingViewProps) {
  // Default the "Open app" destination at render time so SSR + the connected
  // wrapper share the same canonical city app URL (Charlotte for the MVP).
  const resolvedOpenAppHref = openAppHref ?? cityAppEntryUrl(undefined, { isStaging });
  // The Account link is signed-in only and always crosses to the app host —
  // see `accountHref` prop docs for why a host-relative path 404s on the city
  // host.
  const resolvedAccountHref =
    accountHref ?? appUrl({ city: defaultCityCode(), path: '/profile', isStaging });

  return (
    <SiteHeaderShell
      ariaLabel="Landing"
      wordmarkHref="/"
      wordmarkAriaLabel="LOCI Charlotte homepage"
      className={className}
    >
      {/* Wayfinder layer-switcher — the continuity device the DS renders on
        every global-nav layer (apex / city / app). The apex header
        (`SiteHeaderApexView`) and the consumer `AppHeader` both mount it; the
        marketing-city header was the only live surface missing it, leaving the
        layer/city switch reachable only via the Sign-in / Open-app CTAs. It
        sits between the wordmark and the nav to mirror the apex/app layout. */}
      <WayfinderSheet layer="city" cityCode={cityCode} />

      <nav
        aria-label="Main"
        className="flex min-w-0 flex-1 items-center justify-start gap-0.5 overflow-x-auto px-1 sm:justify-center sm:gap-1 sm:overflow-visible"
      >
        {navItems.map((item) => {
          // FRE-827: replace the Categories flat link with the supplied slot
          // (the connected `SiteHeader` passes a `<CategoriesDropdown />`).
          // Detect by `/category` prefix so cross-host-rewritten hrefs
          // (e.g. `https://clt.loci.city/category/music`) match too.
          if (categoriesSlot && /(?:^|\/)category(?:\/|$)/.test(item.href)) {
            return <React.Fragment key={item.href}>{categoriesSlot}</React.Fragment>;
          }
          return (
            <MarketingNavLink
              key={item.href}
              item={item}
              isActive={activeMap?.[item.href] ?? false}
            />
          );
        })}
      </nav>

      {showKbdHint ? <KbdSearchHint /> : null}

      {isSignedIn ? (
        <Link
          href={resolvedAccountHref}
          aria-label="Account"
          className={`inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-full bg-white/10 px-3 py-2 font-display text-small font-semibold text-white/90 hover:bg-white/15 ${FOCUS_RING}`}
        >
          Account
        </Link>
      ) : (
        <div className="flex shrink-0 items-center gap-1 sm:gap-2">
          {/* Native `<a>` (NOT Next `<Link>`): the Sign-in target is an absolute
            cross-host auth URL (`app.loci.city/login`), so it needs the hard
            navigation a plain anchor gives — same as the sibling "Open app"
            anchor below. `force: true` appends `?force=1` so an expired-but-
            present cookie can't bounce a returning visitor off /login. */}
          <a
            href={crossHostLogin(undefined, { force: true, isStaging })}
            className={`inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-full border border-white/[0.16] bg-transparent px-2 py-2 font-display text-small font-semibold text-white/85 transition-colors duration-150 hover:border-white hover:text-white motion-reduce:transition-none sm:px-3 ${FOCUS_RING}`}
          >
            Sign in
          </a>
          <a
            href={resolvedOpenAppHref}
            className={`inline-flex min-h-[44px] desktop:min-h-0 shrink-0 items-center rounded-full bg-gn-accent px-3 py-2 font-display text-small font-semibold text-white transition-colors duration-150 hover:bg-gn-accent-glow motion-reduce:transition-none sm:px-4 ${FOCUS_RING}`}
          >
            Open app
          </a>
        </div>
      )}
    </SiteHeaderShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Apex connected shell (scroll sentinel + view)
// ─────────────────────────────────────────────────────────────────────────────

function SiteHeaderApexMount({ className = '' }: { className?: string }) {
  const { isScrolled, sentinelRef } = useScrolledPast();
  const isStaging = useCityIsStaging();
  return (
    <>
      <div
        ref={sentinelRef}
        data-testid="site-header-apex-scroll-sentinel"
        aria-hidden
        className="pointer-events-none h-20 w-full -mb-20"
      />
      <SiteHeaderApexView isScrolled={isScrolled} isStaging={isStaging} className={className} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Connected SiteHeader
// ─────────────────────────────────────────────────────────────────────────────

export interface SiteHeaderProps {
  /**
   * Surface where the header is mounted. Drives the variant rendered:
   * - `apex`      → apex marketing chrome (transparent → solid on scroll; amber CTA)
   * - `marketing` → `SiteHeaderMarketingView` (Discover/Neighborhoods/This week/Categories nav + Sign-in/Account)
   *
   * There is no `app` surface — the signed-in per-city shell uses `AppHeader`
   * and auth pages use `<AuthShell>`. See {@link SiteHeaderSurface}.
   */
  surface: SiteHeaderSurface;
  className?: string;
}

/**
 * Connected site-wide header. Renders the apex or marketing variant based on
 * `surface`, and reads `useAuth()` to swap "Sign in" → "Account" on the
 * marketing variant when authenticated.
 *
 * Active-link state on the marketing variant is resolved internally via
 * `usePathname()`. The Discover slot uses a dedicated matcher so it activates
 * for both `/discover` (marketing-city surface) and `/<city>/discover` (app
 * surface) pathnames — see `useDiscoverActive`.
 */
export function SiteHeader({ surface, className }: SiteHeaderProps) {
  const { user } = useAuth();
  const citySurface = useCitySurface();
  const city = useCityContext();
  const isStaging = useCityIsStaging();
  const pathname = usePathname();

  // Hooks must be called unconditionally and in a stable order on every render.
  // MARKETING_NAV_ITEMS is module-level and immutable, so the active-state
  // hook calls below run in the same order every render regardless of variant.
  const discoverActive = useDiscoverActive();
  const neighborhoodsActive = useActiveLink('/neighborhoods');
  // The link itself opens Discover `?date=this-week` (WEB-1216 / WEB-1239). The marketing
  // `/this-week` page is still a real route, so landing there keeps this slot
  // current. `useActiveLink` matches pathnames, not query strings, so the
  // discover destination lights the Discover slot instead of this one.
  const thisWeekActive = useActiveLink('/this-week');
  // Categories active state spans both marketing-city (`/category/<slug>`) and
  // app (`/<city>/category/<slug>`) pathnames; the bare-prefix `useActiveLink`
  // misses the app-surface shape, so derive from the slug extractor instead.
  const activeCategorySlug = extractCategorySlugFromPathname(pathname);
  const categoriesActive = activeCategorySlug !== undefined;

  // FRE-825: registers the global ⌘K / `/` shortcut that opens the
  // SearchSheet overlay (`useSearchSheet().setOpen(true, { trigger })`).
  // Mounted here so the shortcut is live on every marketing / apex / auth
  // page that renders the SiteHeader; the consumer surface mounts the same
  // hook via `AppShellLayout → GlobalSearchShortcutMount`. Both mounts
  // converge on the same module-scoped store, so a duplicate mount is a
  // no-op (`setOpen(true)` against an already-open sheet short-circuits).
  //
  // The legacy `useDiscoverSearchFocus()` mount (WEB-79 cross-route nav to
  // `/<city>/discover` + in-page field focus) was removed from this surface
  // — it competed with `useGlobalSearchShortcut` on ⌘K and routed the user
  // to a different destination than the AC requires. The in-page focus
  // behavior on `/discover` is still wired via `DiscoverSearchFocusMount`
  // inside `DiscoverTemplate`, so typing into the discover field after
  // landing there continues to work.
  useGlobalSearchShortcut();

  if (surface === 'apex') {
    return <SiteHeaderApexMount className={className} />;
  }

  // Marketing-variant nav items only render canonically on the marketing-city
  // surface (e.g. `clt.loci.city/discover`). When the marketing variant is
  // rendered on the `app` surface — i.e. the unauth `/login`, `/signup`, etc.
  // pages — the bare paths 404 because the app surface routes Discover under
  // `/<city>/discover`. Rewrite hrefs to cross-host back to the marketing-city
  // surface so the nav doesn't dead-end the user. Other surfaces keep the
  // bare-path defaults so stories, tests, and the marketing-city surface itself
  // stay unchanged.
  const navItems: ReadonlyArray<NavItem> =
    citySurface === 'app'
      ? MARKETING_NAV_ITEMS.map((item) => ({
          ...item,
          href: cityUrl(city?.code ?? defaultCityCode(), item.href, { isStaging }),
        }))
      : MARKETING_NAV_ITEMS;

  const activeMap: Record<string, boolean> = Object.fromEntries(
    navItems.map((item, index) => {
      const source = MARKETING_NAV_ITEMS[index];
      switch (source?.href) {
        case '/discover':
          return [item.href, discoverActive];
        case '/neighborhoods':
          return [item.href, neighborhoodsActive];
        case '/discover?date=this-week':
          return [item.href, thisWeekActive];
        case '/category/music':
          return [item.href, categoriesActive];
        default:
          return [item.href, false];
      }
    }),
  );

  // FRE-827: Categories dropdown slot rendered in place of the flat
  // `/category/music` link on the city marketing variant. The wrapper element
  // carries `data-active` / `data-active-slug` so SiteHeader tests + future
  // visual-regression checks can assert active state without coupling to the
  // molecule's internals. Styling for the trigger is overridden via the
  // molecule's `className` passthrough so it matches the glass-pill chrome.
  // Constructed only here (after the `apex` early return) so the
  // CategoriesDropdown React tree is not built for surfaces that never render it.
  //
  // This slot only renders on the marketing-city surface (`<city>.loci.city`),
  // where the molecule's default destination (`/category/<slug>`,
  // host-relative) is exactly right. We must NOT pass a city-prefixed builder
  // here: `/<city>/category/<slug>` 404s on the city host (the city is the
  // subdomain, not a path segment).
  const categoriesSlot = (
    <span
      data-testid="site-header-categories-slot"
      data-active={categoriesActive ? 'true' : 'false'}
      data-active-slug={activeCategorySlug ?? ''}
      className="inline-flex"
    >
      <CategoriesDropdown
        activeSlug={activeCategorySlug}
        className={[
          // Glass-pill marketing styling — overrides the molecule's default
          // consumer-AppHeader pill (text-foreground / no fill). Padding and
          // type-scale mirror MarketingNavLink so the trigger fits the row.
          'min-h-[44px] desktop:min-h-0 shrink-0 rounded-full px-2 py-2 sm:px-3',
          PILL_TYPE,
          'font-medium transition-colors duration-150 motion-reduce:transition-none',
          FOCUS_RING,
          categoriesActive
            ? 'bg-gn-accent-wash text-gn-accent-glow'
            : 'text-white/85 hover:text-white',
        ].join(' ')}
      />
    </span>
  );

  return (
    <SiteHeaderMarketingView
      navItems={navItems}
      activeMap={activeMap}
      isSignedIn={Boolean(user)}
      accountHref={appUrl({
        city: city?.code ?? defaultCityCode(),
        path: '/profile',
        isStaging,
      })}
      categoriesSlot={categoriesSlot}
      cityCode={city?.code ?? defaultCityCode()}
      isStaging={isStaging}
      className={className}
    />
  );
}
